import { useEffect, useState, useCallback } from "react";
import {
    getVendorMenu,
    createMenuItem,
    assignMenuToVendor,
    updateMenuItem,
    deleteMenuItem,
    getFoodTags
} from "../../api/adminApi";

import {
    Button, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    Dialog, DialogTitle, DialogContent, TextField, DialogActions,
    Typography, useTheme, useMediaQuery, Box, Card, CardContent,
    Stack, Chip, TableContainer, MenuItem, Backdrop, CircularProgress,
    Select, OutlinedInput, Checkbox, ListItemText, FormControl,
    InputLabel, FormControlLabel, Switch
} from "@mui/material";

const modernInputStyle = {
    '& .MuiInputLabel-root': { color: '#A59D96', fontWeight: '600', bgcolor: '#FFFFFF', px: 0.5 },
    '& .MuiInputLabel-root.Mui-focused': { color: '#D97706' },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px', bgcolor: '#FFFFFF',
        '& fieldset': { borderColor: '#ECE5DA' },
        '&:hover fieldset': { borderColor: '#F29D17' },
        '&.Mui-focused fieldset': { borderColor: '#F8C332', borderWidth: '2px' },
        '&.Mui-disabled': { bgcolor: '#FFFDF8' }
    },
    '& .MuiOutlinedInput-input': { fontWeight: '700', color: '#2F2A25' }
};

export default function VendorMenu({ vendorId }) {
    const [menu, setMenu] = useState([]);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [availableTags, setAvailableTags] = useState([]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

    const [form, setForm] = useState({
        name: "", category: "Veg", description: "", vendor_price: "",
        selling_price: "", display_price: "", image: null, existing_image: null,
        food_tags: [], is_priority: false, sort_order: "",
    });

    const fetchMenu = useCallback(async () => {
        const res = await getVendorMenu(vendorId);
        setMenu(res.data);
    }, [vendorId]);

    useEffect(() => {
        const fetchTags = async () => {
            try {
                // 👇 NEW: Pass vendor_id to the API so backend knows which zone to filter
                const res = await getFoodTags({ vendor_id: vendorId });
                setAvailableTags(res.data);
            } catch (err) {
                console.error("Failed to load food tags", err);
            }
        };
        if (vendorId) fetchTags(); // Ensure vendorId exists before fetching
    }, [vendorId]);

    useEffect(() => { fetchMenu(); }, [fetchMenu]);

    const filteredMenu = menu.filter((m) => {
        const query = searchQuery.toLowerCase();
        return (
            (m.name && m.name.toLowerCase().includes(query)) ||
            (m.category && m.category.toLowerCase().includes(query))
        );
    });

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append("name", form.name);
            formData.append("category", form.category);
            formData.append("description", form.description || "");
            formData.append("vendor_price", form.vendor_price);
            formData.append("selling_price", form.selling_price);
            formData.append("is_priority", form.is_priority);
            formData.append("sort_order", form.sort_order === "" ? 0 : form.sort_order);
            formData.append("food_tags", JSON.stringify(form.food_tags));

            if (form.display_price !== "" && form.display_price !== null && form.display_price !== undefined) {
                formData.append("display_price", form.display_price);
            }
            if (form.image) formData.append("image", form.image);

            if (editId) {
                await updateMenuItem(editId, formData);
            } else {
                const res = await createMenuItem(formData);
                await assignMenuToVendor(vendorId, { menu_item_id: res.data.id });
            }

            setOpen(false);
            setEditId(null);
            fetchMenu();

        } catch (err) {
            console.error(err);
            const errorDetail = err.response?.data?.detail;
            if (Array.isArray(errorDetail)) {
                alert(`Validation Error: ${errorDetail[0].loc.join(".")} - ${errorDetail[0].msg}`);
            } else {
                alert(errorDetail || err.response?.data?.error || "Failed to save item");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (item) => {
        setEditId(item.id);
        setForm({
            name: item.name || "", category: item.category || "Veg",
            description: item.description || "", vendor_price: item.vendor_price || "",
            selling_price: item.selling_price || "", display_price: item.display_price || "",
            image: null, existing_image: item.image_url || null,
            food_tags: item.food_tags ? item.food_tags.map(t => t.id) : [],
            is_priority: item.is_priority || false, sort_order: item.sort_order || "",
        });
        setOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteId(id);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await deleteMenuItem(deleteId);
            setDeleteDialogOpen(false);
            setDeleteId(null);
            fetchMenu();
        } catch (err) {
            console.error(err);
            alert("Failed to delete item");
        }
    };

    return (
        <>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2} mb={3}>
                <Button
                    variant="contained" disableElevation
                    sx={{
                        whiteSpace: "nowrap", flexShrink: 0, height: 48, borderRadius: '16px',
                        bgcolor: '#F8C332', color: '#3A2C23', fontWeight: "900", textTransform: "none",
                        px: { xs: 2, sm: 3 }, fontSize: { xs: "0.95rem", sm: "1rem" },
                        boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)', '&:hover': { bgcolor: '#F5B71D' }
                    }}
                    onClick={() => {
                        setEditId(null);
                        setForm({
                            name: "", category: "Veg", description: "", vendor_price: "",
                            selling_price: "", display_price: "", image: null, existing_image: null,
                            food_tags: [], is_priority: false, sort_order: "",
                        });
                        setOpen(true);
                    }}
                >
                    + Add Menu Item
                </Button>

                <TextField
                    placeholder={isMobile ? "Search..." : "Search menu..."}
                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} fullWidth={isMobile}
                    sx={{ ...modernInputStyle, "& .MuiOutlinedInput-root": { ...modernInputStyle["& .MuiOutlinedInput-root"], height: 48, minWidth: { sm: "280px" } } }}
                />
            </Stack>

            {isMobile ? (
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {filteredMenu.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '20px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF' }}>
                            <Typography color="text.secondary" fontWeight="700">No menu items found.</Typography>
                        </Paper>
                    ) : (
                        filteredMenu.map((m) => (
                            <Card key={m.id} elevation={0}
                                sx={{ borderRadius: '24px', border: "none", bgcolor: '#FFFFFF', boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)', transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)' } }}
                            >
                                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                                        <Box flex={1}>
                                            <Typography variant="h6" color="#3A2C23" fontWeight="900" sx={{ lineHeight: 1.2, mb: 0.5 }}>{m.name}</Typography>
                                            <Chip label={m.category} size="small" sx={{ mb: 1.5, height: 22, fontSize: "0.7rem", backgroundColor: m.category === "Veg" ? "#DDECC8" : "#FFF0EE", color: m.category === "Veg" ? "#577A34" : "#E85D3F", fontWeight: 800, borderRadius: "6px" }} />
                                            <Typography variant="body2" color="#6E655E" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontSize: "0.85rem", fontWeight: 500, lineHeight: 1.4 }}>
                                                {m.description ? m.description : "No description provided."}
                                            </Typography>
                                        </Box>
                                        {m.image_url && (
                                            <Box flexShrink={0} sx={{ width: 75, height: 75, borderRadius: '12px', overflow: "hidden", boxShadow: "0px 4px 12px rgba(0,0,0,0.08)" }}>
                                                <img src={m.image_url} alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ backgroundColor: "#FFFDF8", p: 1.5, borderRadius: '12px', border: '1px solid #ECE5DA', mt: 2 }}>
                                        <Stack direction="row" spacing={2} mb={1.5}>
                                            <Box flex={1}>
                                                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, display: "block", mb: 0.2 }}>VENDOR PRICE</Typography>
                                                <Typography variant="subtitle2" fontWeight="900" color="#3A2C23">₹ {m.vendor_price}</Typography>
                                            </Box>
                                            <Box flex={1}>
                                                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, display: "block", mb: 0.2 }}>SELLING PRICE</Typography>
                                                <Typography variant="subtitle2" fontWeight="900" color="#E85D3F">₹ {m.selling_price}</Typography>
                                            </Box>
                                        </Stack>

                                        <Stack direction="row" spacing={2}>
                                            <Box flex={1}>
                                                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, display: "block", mb: 0.2 }}>DISPLAY PRICE</Typography>
                                                <Typography variant="body2" sx={{ textDecoration: m.display_price > m.selling_price ? "line-through" : "none", color: "#6E655E", fontWeight: 700 }}>
                                                    {m.display_price > m.selling_price ? `₹ ${m.display_price}` : "-"}
                                                </Typography>
                                            </Box>
                                            <Box flex={1}>
                                                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, display: "block", mb: 0.2 }}>DISCOUNT</Typography>
                                                <Typography variant="body2" color="#577A34" fontWeight="800">
                                                    {m.display_price > m.selling_price ? `${Math.round(((m.display_price - m.selling_price) / m.display_price) * 100)}% OFF` : "-"}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>

                                    <Stack direction="row" spacing={1.5} mt={2.5}>
                                        <Button size="small" variant="outlined" fullWidth onClick={() => handleEdit(m)} sx={{ borderRadius: '12px', height: 40, fontWeight: "800", textTransform: "none", color: '#6E655E', borderColor: '#ECE5DA', '&:hover': { bgcolor: '#FFFDF8', borderColor: '#F8C332', color: '#3A2C23' } }}>
                                            Edit Item
                                        </Button>
                                        <Button size="small" variant="contained" disableElevation fullWidth onClick={() => handleDelete(m.id)} sx={{ borderRadius: '12px', height: 40, fontWeight: "800", textTransform: "none", bgcolor: '#FFF0EE', color: '#E85D3F', border: '1px solid #FFE4E0', '&:hover': { bgcolor: '#FFE4E0' } }}>
                                            Delete
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </Stack>
            ) : (
                <TableContainer
                    component={Paper} elevation={0}
                    sx={{ mt: 2, overflowX: "auto", borderRadius: '24px', border: "none", bgcolor: '#FFFFFF', boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)', transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)' } }}
                >
                    <Table>
                        <TableHead sx={{ bgcolor: "#FFFDF8" }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Vendor Price</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Selling Price</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Display Price</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Discount</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Image</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredMenu.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary', fontWeight: 700 }}>No menu items found.</TableCell>
                                </TableRow>
                            ) : (
                                filteredMenu.map((m) => (
                                    <TableRow key={m.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                        <TableCell sx={{ fontWeight: "900", color: '#3A2C23' }}>{m.name}</TableCell>
                                        <TableCell><Chip label={m.category} size="small" sx={{ backgroundColor: m.category === "Veg" ? "#DDECC8" : "#FFF0EE", color: m.category === "Veg" ? "#577A34" : "#E85D3F", fontWeight: 800, borderRadius: '6px' }} /></TableCell>
                                        <TableCell sx={{ color: '#6E655E', fontWeight: 600 }}>{m.description ? m.description.slice(0, 40) + (m.description.length > 40 ? "..." : "") : "-"}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: '#3A2C23' }}>₹ {m.vendor_price}</TableCell>
                                        <TableCell sx={{ color: "#E85D3F", fontWeight: "900" }}>₹ {m.selling_price}</TableCell>
                                        <TableCell sx={{ color: "#A59D96", fontWeight: 700 }}>{m.display_price > m.selling_price ? (<span style={{ textDecoration: 'line-through' }}>₹ {m.display_price}</span>) : "-"}</TableCell>
                                        <TableCell sx={{ color: "#577A34", fontWeight: "900" }}>{m.display_price > m.selling_price ? `${Math.round(((m.display_price - m.selling_price) / m.display_price) * 100)}% OFF` : "-"}</TableCell>
                                        <TableCell>
                                            {m.image_url ? (
                                                <img src={m.image_url} alt={m.name} width={50} height={50} style={{ borderRadius: '8px', objectFit: "cover", boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
                                            ) : (
                                                <Typography variant="body2" color="#A59D96" fontWeight="700">-</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Button onClick={() => handleEdit(m)} size="small" variant="contained" disableElevation sx={{ mr: 1, borderRadius: '8px', bgcolor: '#FFF6DE', color: '#F29D17', fontWeight: 800, textTransform: 'none', '&:hover': { bgcolor: '#F8C332', color: '#3A2C23' } }}>Edit</Button>
                                            <Button onClick={() => handleDelete(m.id)} size="small" variant="contained" disableElevation sx={{ borderRadius: '8px', bgcolor: '#FFF0EE', color: '#E85D3F', fontWeight: 800, textTransform: 'none', '&:hover': { bgcolor: '#FFE4E0' } }}>Delete</Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={open} onClose={() => !isLoading && setOpen(false)} fullWidth maxWidth="sm" fullScreen={fullScreenDialog} scroll="body" PaperProps={{ sx: { borderRadius: fullScreenDialog ? 0 : '24px', bgcolor: '#FFFDF8', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' } }}>
                <DialogTitle sx={{ fontWeight: 900, pb: 2, pt: 3, px: { xs: 2, sm: 4 }, color: '#3A2C23', borderBottom: '1px dashed #ECE5DA' }}>{editId ? "Edit Menu Item" : "Add Menu Item"}</DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 4 }, pt: "24px !important", pb: { xs: '15vh', sm: 4 } }}>
                    <Stack spacing={2.5}>
                        <TextField label="Name" fullWidth sx={modernInputStyle} value={form.name} disabled={isLoading} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        <FormControl fullWidth sx={modernInputStyle}>
                            <InputLabel>Category</InputLabel>
                            <Select label="Category" value={form.category} disabled={isLoading} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                <MenuItem value="Veg" sx={{ fontWeight: 700 }}>Veg</MenuItem>
                                <MenuItem value="Non Veg" sx={{ fontWeight: 700 }}>Non Veg</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Description" fullWidth multiline rows={3} sx={modernInputStyle} value={form.description} disabled={isLoading} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
                            <TextField label="Vendor Price" type="number" fullWidth sx={modernInputStyle} value={form.vendor_price} disabled={isLoading} onChange={(e) => setForm({ ...form, vendor_price: e.target.value === "" ? "" : parseFloat(e.target.value) })} />
                            <TextField label="Selling Price" type="number" fullWidth sx={modernInputStyle} value={form.selling_price} disabled={isLoading} onChange={(e) => setForm({ ...form, selling_price: e.target.value === "" ? "" : parseFloat(e.target.value) })} />
                        </Stack>
                        <TextField label="Display Price (Optional)" type="number" fullWidth sx={modernInputStyle} value={form.display_price} disabled={isLoading} onChange={(e) => setForm({ ...form, display_price: e.target.value === "" ? "" : parseFloat(e.target.value) })} />

                        <Box sx={{ bgcolor: "#FFFFFF", p: 2, borderRadius: '16px', border: "1px solid #ECE5DA", boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                            <FormControlLabel
                                control={<Switch checked={form.is_priority} onChange={(e) => setForm({ ...form, is_priority: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#F8C332' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#F8C332' } }} />}
                                label={<Typography variant="body2" fontWeight="700" color="#3A2C23">Mark as Priority (Show at top of user app)</Typography>}
                            />
                        </Box>

                        {form.is_priority && (
                            <TextField label="Priority Rank (1 = Top, 2 = Second, etc.)" type="number" fullWidth sx={modernInputStyle} value={form.sort_order} disabled={isLoading} onChange={(e) => { const val = e.target.value; setForm({ ...form, sort_order: val === "" ? "" : parseInt(val) }); }} />
                        )}

                        <FormControl fullWidth disabled={isLoading} sx={modernInputStyle}>
                            <InputLabel>Food Tags (Optional)</InputLabel>
                            <Select
                                multiple value={form.food_tags} onChange={(e) => setForm({ ...form, food_tags: e.target.value })} input={<OutlinedInput label="Food Tags (Optional)" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((val) => {
                                            const tag = availableTags.find(t => t.id === val);
                                            return <Chip key={val} label={tag?.name || val} size="small" sx={{ fontWeight: 700, bgcolor: '#FFFDF8', border: '1px solid #ECE5DA' }} />;
                                        })}
                                    </Box>
                                )}
                            >
                                {availableTags.map((tag) => (
                                    <MenuItem key={tag.id} value={tag.id} sx={{ fontWeight: 600 }}>
                                        <Checkbox checked={form.food_tags.indexOf(tag.id) > -1} sx={{ color: '#F29D17', '&.Mui-checked': { color: '#F8C332' } }} />
                                        <ListItemText primary={tag.name} primaryTypographyProps={{ fontWeight: 600, color: '#3A2C23' }} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box sx={{ border: '1px dashed #ECE5DA', borderRadius: '16px', p: 3, bgcolor: '#FFFFFF', textAlign: 'center' }}>
                            <Typography variant="body2" color="#3A2C23" mb={1.5} fontWeight="800">Upload Image</Typography>
                            <input type="file" accept="image/*" disabled={isLoading} onChange={(e) => setForm({ ...form, image: e.target.files[0] })} style={{ fontFamily: 'inherit', fontSize: '0.85rem', color: '#6E655E', cursor: 'pointer' }} />
                            {form.image ? (
                                <Box mt={2}><img src={URL.createObjectURL(form.image)} alt="new preview" width={100} style={{ borderRadius: 12, opacity: isLoading ? 0.5 : 1, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} /></Box>
                            ) : form.existing_image ? (
                                <Box mt={2}>
                                    <img src={form.existing_image} alt="existing preview" width={100} style={{ borderRadius: 12, opacity: isLoading ? 0.5 : 1, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                    <Typography variant="caption" display="block" color="#A59D96" fontWeight="700" mt={1}>Current Image (Upload a new file to replace)</Typography>
                                </Box>
                            ) : null}
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: { xs: 2, sm: 3 }, px: { sm: 4 }, borderTop: "1px solid #ECE5DA", bgcolor: '#FFFFFF', borderRadius: '0 0 24px 24px' }}>
                    <Button variant="outlined" disabled={isLoading} onClick={() => { setOpen(false); setEditId(null); }} sx={{ textTransform: "none", fontWeight: 800, color: '#6E655E', borderColor: '#ECE5DA', borderRadius: '12px', px: 3 }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={isLoading} disableElevation sx={{ textTransform: "none", fontWeight: 900, borderRadius: '12px', bgcolor: '#F8C332', color: '#3A2C23', px: 4, py: 1.2, boxShadow: isLoading ? 'none' : '0 4px 12px rgba(248, 195, 50, 0.3)', '&:hover': { bgcolor: '#F5B71D' } }}>
                        {isLoading ? <CircularProgress size={24} sx={{ color: '#3A2C23' }} /> : (editId ? "Update" : "Create")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '24px', bgcolor: '#FFFDF8' } }}>
                <DialogTitle sx={{ fontWeight: 900, color: '#3A2C23', pb: 1, pt: 3, textAlign: 'center' }}>Delete Menu Item?</DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <Typography align="center" color="#6E655E" fontWeight="600" fontSize="0.9rem">Are you sure you want to delete this menu item? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", gap: 1.5, pb: 3, px: 3 }}>
                    <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 800, color: '#6E655E', borderColor: '#ECE5DA', textTransform: 'none', borderRadius: '12px', flex: 1 }}>Cancel</Button>
                    <Button variant="contained" color="error" disableElevation onClick={confirmDelete} sx={{ fontWeight: 900, borderRadius: '12px', textTransform: 'none', flex: 1, bgcolor: '#DC2626', color: '#FFFFFF', '&:hover': { bgcolor: '#B91C1C' } }}>Yes, Delete</Button>
                </DialogActions>
            </Dialog>

            <Backdrop sx={{ color: '#fff', zIndex: (theme) => Math.max(theme.zIndex.modal, theme.zIndex.drawer) + 10, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: 'rgba(58, 44, 35, 0.8)' }} open={isLoading}>
                <CircularProgress sx={{ color: '#F8C332' }} />
                <Typography variant="h6" fontWeight="900" sx={{ color: '#FFFDF8' }}>{editId ? "Updating..." : "Creating..."}</Typography>
            </Backdrop>
        </>
    );
}